/**
 * Shared panels for Overview + Outlook: pacing gauges, the
 * light-months alert strip, and the forward-book panels (quality by
 * stage, stage totals, next 90 days).
 */
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer,
  Tooltip as RechartsTooltip, Legend,
} from "recharts";
import { AlertTriangle, CheckCircle2, ExternalLink } from "lucide-react";
import { fmtGbp, fmtGbpShort, fmtDate, PanelTitle } from "../staff/CommissionedPanels";
import { InfoTip } from "./cfoShared";

const fmtMonth = (ym) => {
  const [y, m] = ym.split("-").map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString("en-GB", { month: "short", year: "2-digit" });
};

const STATUS_TONE = {
  "SOW SIGNED": "text-violet-800 bg-violet-50 border-violet-200",
  "BRIEFED": "text-indigo-800 bg-indigo-50 border-indigo-200",
  "IN FIELD": "text-amber-800 bg-amber-50 border-amber-200",
  "INVOICED": "text-teal-800 bg-teal-50 border-teal-200",
};

export function StatusBadge({ status }) {
  return (
    <span className={`inline-block text-[9px] font-mono uppercase px-1.5 py-0.5 rounded border ${STATUS_TONE[status] || "text-stone-600 bg-stone-50 border-stone-200"}`}>
      {status}
    </span>
  );
}

function DeltaBadge({ pct }) {
  if (pct == null) return null;
  const up = pct >= 0;
  return (
    <span className={`inline-flex items-center gap-0.5 text-[10px] font-mono px-1.5 py-0.5 rounded border ${up ? "text-teal-800 bg-teal-50 border-teal-200" : "text-rose-800 bg-rose-50 border-rose-200"}`}>
      {up ? "▲" : "▼"} {Math.abs(pct)}% vs last year
    </span>
  );
}

export function PaceCard({ p, testid }) {
  const max = Math.max(p.projection, p.last_year, 1);
  const w = (v) => `${Math.max(Math.min((v / max) * 100, 100), 0)}%`;
  return (
    <section data-testid={testid} className="bg-white rounded-2xl border border-stone-200 p-5">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <PanelTitle>Pacing · {p.label} <InfoTip id="ov_pacing" /></PanelTitle>
        <DeltaBadge pct={p.delta_pct} />
      </div>
      <p className="text-2xl font-medium tabular-nums text-slate-900 leading-none mt-1">
        {fmtGbpShort(p.projection)}
        <span className="text-xs font-normal text-stone-400 ml-1.5">projected</span>
      </p>
      <div className="relative h-2.5 rounded-full bg-stone-100 mt-3 overflow-visible">
        <div className="absolute inset-y-0 left-0 rounded-l-full bg-teal-600" style={{ width: w(p.billed_so_far) }} />
        <div className="absolute inset-y-0 bg-[#3EA8DC] opacity-60" style={{ left: w(p.billed_so_far), width: w(p.scheduled_remainder) }} />
        {p.last_year > 0 && (
          <div className="absolute -top-1 -bottom-1 w-0.5 bg-slate-800" style={{ left: w(p.last_year) }} title={`last year: ${fmtGbpShort(p.last_year)}`} />
        )}
      </div>
      <p className="text-[10px] font-mono text-stone-400 mt-2">
        <span className="text-teal-700">{fmtGbpShort(p.billed_so_far)} billed</span>
        {" · "}<span className="text-sky-700">{fmtGbpShort(p.scheduled_remainder)} scheduled</span>
        {" · "}last year {fmtGbpShort(p.last_year)} <span className="text-slate-700">▏</span>
      </p>
      {p.next?.length > 0 && (
        <div className="mt-4 pt-3 border-t border-stone-100" data-testid={`${testid}-next`}>
          <p className="text-[10px] font-mono uppercase tracking-wider text-stone-400 mb-2">
            Next {p.next.length} {p.next.length === 2 ? "quarters" : "months"} · scheduled vs last year
          </p>
          <ul className="space-y-2">
            {p.next.map((n) => {
              const nm = Math.max(n.projection, n.last_year, 1);
              return (
                <li key={n.period} data-testid={`${testid}-next-${n.period}`}>
                  <div className="flex items-center justify-between text-[11px] gap-2">
                    <span className="font-mono text-slate-700 shrink-0">{n.label}</span>
                    <span className="font-mono tabular-nums text-stone-500 truncate">
                      <span className="text-sky-700">{fmtGbpShort(n.projection)}</span>
                      {" "}sched · LY {fmtGbpShort(n.last_year)}
                      {n.delta_pct != null && (
                        <span
                          className={`ml-1.5 ${n.delta_pct >= 0 ? "text-teal-700" : "text-rose-600"}`}
                          title={`Scheduled so far vs the same period last year's billed total (${fmtGbpShort(n.last_year)}).`}
                        >
                          {n.delta_pct >= 0 ? "▲" : "▼"}{Math.abs(n.delta_pct)}%
                        </span>
                      )}
                    </span>
                  </div>
                  <div className="relative h-1.5 rounded-full bg-stone-100 mt-1">
                    <div
                      className="absolute inset-y-0 left-0 rounded-full bg-[#3EA8DC] opacity-70"
                      style={{ width: `${Math.max(Math.min((n.projection / nm) * 100, 100), n.projection > 0 ? 2 : 0)}%` }}
                    />
                    {n.last_year > 0 && (
                      <div
                        className="absolute -top-0.5 -bottom-0.5 w-0.5 bg-slate-800"
                        style={{ left: `${Math.min((n.last_year / nm) * 100, 100)}%` }}
                        title={`last year: ${fmtGbpShort(n.last_year)}`}
                      />
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
          <p className="text-[10px] text-stone-400 mt-2">
            Future periods show what is already scheduled - more lands as new work is commissioned.
          </p>
        </div>
      )}
    </section>
  );
}

const STAGE_COLORS = {
  "IN FIELD": "#D97706",
  "BRIEFED": "#6366F1",
  "SOW SIGNED": "#7C3AED",
  "INVOICED": "#0D9488",
};

export function ForwardQualityPanel({ fq }) {
  const chart = fq.monthly.map((m) => ({ label: fmtMonth(m.month), ...m.stages }));
  return (
    <section data-testid="forward-quality-panel" className="bg-white rounded-2xl border border-stone-200 p-5">
      <PanelTitle>Forward book quality · next 12 months by stage <InfoTip id="fq_quality" /></PanelTitle>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 h-56">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chart} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
              <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#a8a29e" }} axisLine={false} tickLine={false} interval={0} />
              <YAxis tickFormatter={(v) => fmtGbpShort(v)} tick={{ fontSize: 10, fill: "#a8a29e" }} axisLine={false} tickLine={false} width={54} />
              <RechartsTooltip contentStyle={{ fontSize: 11, borderRadius: 8 }} formatter={(v, n) => [fmtGbpShort(v), n]} />
              <Legend wrapperStyle={{ fontSize: 10 }} />
              {fq.stages.map((s) => (
                <Bar key={s} dataKey={s} stackId="fq" fill={STAGE_COLORS[s] || "#a8a29e"} maxBarSize={22} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div>
          <p className="text-[10px] font-mono uppercase tracking-wider text-stone-500 mb-1">
            Concentration risk <InfoTip id="fq_concentration" className="ml-0.5" />
          </p>
          <p className="text-2xl font-medium tabular-nums text-slate-900 leading-none">
            {fq.top5_share_pct != null ? `${fq.top5_share_pct}%` : "–"}
          </p>
          <p className="text-[10px] font-mono text-stone-400 mt-1 mb-3">
            of the {fmtGbpShort(fq.forward_total)} forward book sits with 5 clients
          </p>
          <ul className="space-y-1.5">
            {fq.top_clients.map((c) => (
              <li key={c.client} className="flex items-center justify-between text-xs gap-2">
                <span className="font-medium text-slate-800 truncate">{c.client}</span>
                <span className="font-mono tabular-nums text-stone-500 shrink-0">
                  {fmtGbpShort(c.value)} · {c.share_pct}%
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>
      <p className="text-[10px] text-stone-400 mt-2">
        IN FIELD money is nearly certain; SOW SIGNED sits further from delivery. Stage is the project's status.
      </p>
    </section>
  );
}

export function ForwardStagePanel({ byStatus }) {
  const max = Math.max(...byStatus.map((x) => x.value), 1);
  return (
    <section data-testid="revenue-forward-breakdown" className="bg-white rounded-2xl border border-stone-200 p-5">
      <PanelTitle>Forward book by stage <InfoTip id="fwd_stage" /></PanelTitle>
      <ul className="space-y-2">
        {byStatus.map((s) => (
          <li key={s.status} data-testid={`revenue-stage-${s.status.replace(/\s+/g, "-").toLowerCase()}`}>
            <div className="flex items-center justify-between text-xs mb-0.5">
              <StatusBadge status={s.status} />
              <span className="font-mono tabular-nums text-stone-600">
                {fmtGbpShort(s.value)} · {s.count} inv
              </span>
            </div>
            <div className="h-1.5 rounded-full bg-stone-100">
              <div
                className="h-full rounded-full bg-gradient-to-r from-[#1B4F8A] to-[#3EA8DC]"
                style={{ width: `${Math.max((s.value / max) * 100, 2)}%` }}
              />
            </div>
          </li>
        ))}
      </ul>
      <p className="text-[10px] text-stone-400 mt-3">
        Stage is the project's status - instalments scheduled on projects
        already at INVOICED are final/post-dated invoices.
      </p>
    </section>
  );
}

export function Next90Panel({ upcoming }) {
  return (
    <section data-testid="revenue-upcoming-panel" className="bg-white rounded-2xl border border-stone-200 p-5">
      <PanelTitle>
        Next 90 days · {upcoming.length} instalment{upcoming.length === 1 ? "" : "s"} · {fmtGbpShort(upcoming.reduce((s, i) => s + (i.value || 0), 0))}
        {" "}<InfoTip id="next_90" />
      </PanelTitle>
      <div className="max-h-80 overflow-y-auto pr-1">
        <ul className="divide-y divide-stone-50">
          {upcoming.map((i) => (
            <li key={i.fiid || `${i.cid}-${i.n}`} data-testid={`revenue-upcoming-${i.fiid || i.cid}`} className="py-2 flex items-center gap-3">
              <div className="min-w-0 flex-1">
                <p className="text-xs font-medium text-slate-800 truncate">
                  {i.client_company || "Unknown"}
                  <span className="text-stone-400 font-normal">
                    {" "}· instalment {i.n}{i.expected_invoices ? ` of ${i.expected_invoices}` : ""}
                  </span>
                </p>
                <p className="text-[10px] text-stone-500 truncate">
                  {i.project_name || ""}
                  {i.cognito_admin && (
                    <a href={i.cognito_admin} target="_blank" rel="noreferrer" className="ml-1.5 inline-flex items-center gap-0.5 font-mono text-stone-400 hover:text-[#1B4F8A]">
                      #{i.cid}<ExternalLink className="w-2.5 h-2.5" />
                    </a>
                  )}
                </p>
              </div>
              <StatusBadge status={i.status} />
              <div className="text-right shrink-0">
                <p className="text-xs font-mono tabular-nums text-slate-800">{i.value != null ? fmtGbp(i.value) : "-"}</p>
                <p className="text-[10px] font-mono tabular-nums text-stone-400">{fmtDate(i.date)}</p>
              </div>
            </li>
          ))}
          {upcoming.length === 0 && (
            <li className="py-3 text-xs text-stone-400">No instalments scheduled in the next 90 days.</li>
          )}
        </ul>
      </div>
    </section>
  );
}

export function LightMonthsStrip({ light }) {
  if (!light || !light.run_rate) return null;
  if (!light.months.length) {
    return (
      <div data-testid="light-months-ok" className="rounded-xl border border-teal-200 bg-teal-50/60 px-4 py-2.5 text-xs text-teal-900 flex items-center gap-2">
        <CheckCircle2 className="w-4 h-4 shrink-0" />
        <span>
          No light months ahead - all of the next 6 months have at least {light.threshold_pct}% of the{" "}
          {fmtGbpShort(light.run_rate)}/mo run-rate already scheduled.
        </span>
        <InfoTip id="ov_light_months" />
      </div>
    );
  }
  return (
    <div data-testid="light-months-alert" className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-900">
      <div className="flex items-center gap-2 font-medium">
        <AlertTriangle className="w-4 h-4 shrink-0" />
        <span>
          {light.months.length} light month{light.months.length === 1 ? "" : "s"} in the next 6 - scheduled billing
          below {light.threshold_pct}% of the {fmtGbpShort(light.run_rate)}/mo run-rate. New work must land here or cash dips.
        </span>
        <InfoTip id="ov_light_months" />
      </div>
      <div className="flex flex-wrap gap-2 mt-2 ml-6">
        {light.months.map((m) => (
          <span key={m.month} data-testid={`light-month-${m.month}`} className="inline-flex items-baseline gap-1.5 rounded-lg border border-amber-300 bg-white px-2 py-1 font-mono">
            <span className="font-semibold">{fmtMonth(m.month)}</span>
            <span>{fmtGbpShort(m.scheduled)} scheduled</span>
            <span className="text-rose-700">−{fmtGbpShort(m.gap_to_run_rate)} vs run-rate</span>
          </span>
        ))}
      </div>
    </div>
  );
}
